import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Search,
  ShoppingCart,
  Globe,
  User,
  LogOut,
  LayoutDashboard,
  Upload,
  Shield,
  Library,
  ChevronDown,
  Camera,
} from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export function Header() {
  const { t, lang, toggleLang } = useI18n();
  const { user, logout, openAuth } = useAuth();
  const { count, setOpen } = useCart();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/browse?q=${encodeURIComponent(q)}`);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900">
            <Camera size={18} className="text-white" />
          </div>
          <span className="font-display text-xl font-extrabold tracking-tight text-slate-900">
            Lumina<span className="text-blue-600">Stock</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 text-sm font-medium text-slate-600">
          <Link to="/browse" className="rounded-lg px-3 py-2 hover:bg-slate-100 transition" data-testid="nav-browse">
            {t("nav.browse")}
          </Link>
          <Link to="/browse?type=photo" className="rounded-lg px-3 py-2 hover:bg-slate-100 transition" data-testid="nav-photos">
            {t("nav.photos")}
          </Link>
          <Link to="/browse?type=graphic" className="rounded-lg px-3 py-2 hover:bg-slate-100 transition" data-testid="nav-graphics">
            {t("nav.graphics")}
          </Link>
        </nav>

        <form onSubmit={submitSearch} className="relative hidden md:flex flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("nav.searchPlaceholder")}
            className="w-full rounded-full border border-slate-200 bg-slate-50 py-2 pl-9 pr-4 text-sm outline-none focus:border-blue-500 focus:bg-white transition"
            data-testid="header-search-input"
          />
        </form>

        <div className="flex items-center gap-1.5 ml-auto">
          <button
            onClick={toggleLang}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition"
            data-testid="language-toggle-btn"
          >
            <Globe size={14} />
            {lang.toUpperCase()}
          </button>

          <button
            onClick={() => setOpen(true)}
            className="relative rounded-lg p-2 text-slate-700 hover:bg-slate-100 transition"
            data-testid="cart-toggle-btn"
          >
            <ShoppingCart size={20} />
            {count > 0 && (
              <span
                className="absolute -right-0.5 -top-0.5 flex h-4.5 min-w-[18px] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-bold text-white"
                data-testid="cart-count-badge"
              >
                {count}
              </span>
            )}
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 py-1 pl-1 pr-2 hover:bg-slate-100 transition"
                  data-testid="user-menu-btn"
                >
                  <div className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-900 text-xs font-bold text-white">
                    {user.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                  <ChevronDown size={14} className="text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user.name}</div>
                  <div className="text-xs font-normal text-slate-500 capitalize">{user.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/library")} data-testid="menu-library">
                  <Library size={15} className="mr-2" /> {t("nav.library")}
                </DropdownMenuItem>
                {(user.role === "contributor" || user.role === "admin") && (
                  <DropdownMenuItem onClick={() => navigate("/studio")} data-testid="menu-studio">
                    <Upload size={15} className="mr-2" /> {t("nav.studio")}
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <Shield size={15} className="mr-2" /> {t("nav.admin")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut size={15} className="mr-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => openAuth("login")}
                className="hidden sm:block rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition"
                data-testid="login-btn"
              >
                {t("nav.login")}
              </button>
              <button
                onClick={() => openAuth("register")}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 transition"
                data-testid="register-btn"
              >
                {t("nav.register")}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
